import { OrthographicCamera, PerspectiveCamera, Scene, Vector2, AxesHelper, Vector3, WebGLRenderer, sRGBEncoding, Clock, PCFSoftShadowMap, Group, Raycaster } from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';

import { Basics, isDebug, isSmartphone, isTouch } from '../core/Basics';
import { GetBy } from '../core/Element';
import { Metrics } from '../core/Metrics';
import { Sizes } from '../core/Sizes';
import { CENTER, SETTINGS } from '../../../3D/constants';
import { Interaction } from '../core/Interaction';
import { Maths } from '../utils/Maths';

export default class WebGLSketch {
	_started = false;
	_paused = false;
	static renderer;
	static domElement;
	static scene;
	static camera;
	static clock;
	static size;
	static controls;
	// raycaster;

	static group = new Group();
	static mouse = new Vector2();
	static cameraGroup = new Group();
	static cameraPosition = CENTER;
	static cameraLookAt = CENTER;
	static cameraLookAtTarget = CENTER;

	static raycaster = new Raycaster();
	static _rayMouse = new Vector2();
	static interactives = [];
	static _isRaycast = false;
	static _raycastEnabled = false;

	static _calls = {
		click: (e) => WebGLSketch.click(e)
	};

	static defaults = {
		container: 'scene-three',
		antialias: true,
		alpha: false,
		ortho: false,
		camera2D: SETTINGS.camera2D,
		shadows: false,
		debug: isDebug,
		transparent: true,
		clearColorAlpha: 0,
		lookAt: SETTINGS.lookAt,
		cameraPos: SETTINGS.cameraPos,
		fov: SETTINGS.fov,
		controls: SETTINGS.controls,
		far: SETTINGS.far,
		near: SETTINGS.near,
		clearColor: SETTINGS.clearColor,
		clearColorAlpha: SETTINGS.clearColorAlpha,
	}

	static init(opts = {}) {
		this.defaults = {
			...this.defaults,
			...opts
		};

		this.domElement = GetBy.id(this.defaults.container);
		const { width, height } = this.domElement.getBoundingClientRect();
		this.size = this.defaults.size ? this.defaults.size : new Vector2(width, height);
		this.scene = new Scene();

		this.renderer = new WebGLRenderer({
			canvas: this.domElement,
			antialias: this.defaults.antialias,
			alpha: this.defaults.alpha
		});

		this.renderer.setClearColor(this.defaults.clearColor, this.defaults.clearColorAlpha);
		this.renderer.setSize(this.size.x, this.size.y);
		this.renderer.setPixelRatio(Sizes.RATIO_CANVAS);

		if (this.defaults.outputEncoding) {
			this.renderer.outputEncoding = this.defaults.outputEncoding;
		}

		if (this.defaults.shadows) {
			this.renderer.shadowMap.enabled = true;
			this.renderer.shadowMap.type = PCFSoftShadowMap;
		}

		// if (this.defaults.ortho) {
		// 	this.camera = new OrthographicCamera(
		// 		-this.size.x / 2,
		// 		this.size.x / 2,
		// 		this.size.y / 2,
		// 		-this.size.y / 2,
		// 		this.defaults.near,
		// 		this.defaults.far,
		// 	);
		// } else {
		this.camera = new PerspectiveCamera(
			this.defaults.fov,
			this.size.x / this.size.y,
			this.defaults.near,
			this.defaults.far
		);

		this.camera.position.copy(CENTER);
		this.cameraPosition.copy(this.defaults.cameraPos);
		this.cameraGroup.position.copy(this.defaults.cameraPos);

		this.camera.lookAt(this.defaults.lookAt);
		this.cameraLookAt.copy(this.defaults.lookAt);
		this.cameraLookAtTarget.copy(this.defaults.lookAt);

		if (this.defaults.camera2D) {
			this.camera.fov = 2 * Math.atan(this.size.x / this.camera.aspect / (2 * this.defaults.cameraPos.z)) * (180 / Math.PI);
		}

		this.camera.updateProjectionMatrix();
		// }

		this.cameraGroup.add(this.camera);
		this.scene.add(this.cameraGroup);

		// if (this.defaults.debug) {
		// 	const axesHelper = new AxesHelper(2000);
		// 	this.scene.add(axesHelper);
		// }

		this.controls = new OrbitControls(this.camera, this.domElement);
		this.controls.enableDamping = true;
		this.controls.enabled = this.defaults.controls;

		this.clock = new Clock();

		this.scene.add(this.group);

		// Raycaster ha de detectar objectes a tots els layers (layer 0 + PROD_LAYER 4)
		this.raycaster.layers.enableAll();
	}

	static time() {
		return this.clock.getElapsedTime();
	}

	static start() {
		if (this._started && !this._paused) return;

		if (!this._started) this.addEventListeners();

		this._paused = false;
		this._started = true;
		this.clock.start();

		if (this.defaults.controls) this.domElement.style.pointerEvents = 'initial';
		else this.domElement.style.pointerEvents = 'none';
	}

	static stop() {
		if (!this._started) return;
		if (this._paused) return;
		this._paused = true;
		this.clock.stop();
	}

	static addInteractive(mesh) {
		if (!this.interactives.includes(mesh)) this.interactives.push(mesh);
	}

	static removeInteractive(mesh) {
		this.interactives = this.interactives.filter(o => o !== mesh);
	}

	static enableRaycast() {
		this._raycastEnabled = true;
	}

	static disableRaycast() {
		this._raycastEnabled = false;
		this.reset();
	}

	static raycast() {
		if (this.interactives.length === 0) return;
		if (!this._raycastEnabled) return;

		const { x, y } = Interaction.positions.mouse;
		this._rayMouse.x = (x / Metrics.WIDTH) * 2 - 1;
		this._rayMouse.y = -(y / Metrics.HEIGHT) * 2 + 1;

		this.raycaster.setFromCamera(this._rayMouse, this.camera);
		const intersects = this.raycaster.intersectObjects(this.interactives, true);

		if (intersects.length > 0 && intersects[0].object.userData?.isClickable) {
			this._isRaycast = true;
			this.hoverIn();
		}
	}

	static reset() {
		if (!this._isRaycast) return;
		this._isRaycast = false;
		this.hoverOut();
	}

	static click(e) {
		if (!this._started || this._paused || !this._raycastEnabled || this.interactives.length === 0) return;

		let cx, cy;
		if (e.changedTouches?.length) { cx = e.changedTouches[0].clientX; cy = e.changedTouches[0].clientY; }
		else { cx = e.clientX; cy = e.clientY; }

		this._rayMouse.x = (cx / Metrics.WIDTH) * 2 - 1;
		this._rayMouse.y = -(cy / Metrics.HEIGHT) * 2 + 1;

		this.raycaster.setFromCamera(this._rayMouse, this.camera);
		const intersects = this.raycaster.intersectObjects(this.interactives, true);

		if (intersects.length > 0) {
			const first = intersects[0].object;
			if (first.userData?.isClickable && first.userData?.onClick) {
				first.userData.onClick(first);
			}
		}
	}

	static hoverIn() {
		document.body.style.cursor = 'pointer';
	}

	static hoverOut() {
		document.body.style.cursor = '';
	}

	static addEventListeners() {
		window.addEventListener(Basics.clickEvent, this._calls.click);
	}

	static removeEventListeners() {
		window.removeEventListener(Basics.clickEvent, this._calls.click);
	}

	static update() {
		if (!this._started) return;
		if (this._paused) return;

		this.reset();
		this.raycast();

		if (this.defaults.controls) {
			this.cameraGroup.position.copy(SETTINGS.cameraPos);
			this.controls.update();
			return;
		}

		if (!isTouch) {
			// Mouse
			const center = {
				x: Interaction.positions.mouse.x - Metrics.CENTER_X,
				y: Interaction.positions.mouse.y - Metrics.CENTER_Y
			};
			// Group rotation
			const mx = Maths.map(center.x, - Metrics.CENTER_X, Metrics.CENTER_X, -5, SETTINGS.mouseX);
			const my = - Maths.map(center.y, - Metrics.CENTER_Y, Metrics.CENTER_Y, -SETTINGS.mouseY, SETTINGS.mouseY);
			this.mouse.x = Maths.lerp(this.mouse.x, mx, SETTINGS.mouseLerp);
			this.mouse.y = Maths.lerp(this.mouse.y, my, SETTINGS.mouseLerp);

			const rotationY = Maths.toRadians(this.mouse.x);
			const rotationX = - Maths.toRadians(this.mouse.y);

			if (SETTINGS.groupRotation) {
				this.group.rotation.x = rotationX;
				this.group.rotation.y = rotationY;
			}

			if (SETTINGS.cameraRotation) {
				this.cameraPosition.x += rotationX;
				this.cameraPosition.y += rotationY;
			}
		}

		if (!this.defaults.ortho) {
			this.cameraGroup.position.lerp(this.cameraPosition, SETTINGS.cameraLerp);

			this.cameraLookAt.lerp(this.cameraLookAtTarget, SETTINGS.cameraLerp);
			this.camera.lookAt(this.cameraLookAt);
		}
	}

	static render() {
		this.renderer.render(this.scene, this.camera);
	}

	// Renderitza l'escena en 4 passes per AÏLLAR les llums per grup.
	// IMPORTANT: a three.js els layers de les LLUMS es filtren contra la CÀMERA (no per
	// mesh). Per tant, si la càmera té dos layers de llum actius alhora, totes dues
	// famílies il·luminen tots els objectes. La única manera d'aïllar és un pass per
	// grup amb la càmera a UN sol layer:
	//   Pass 1 (layer 0): character + thumbnails → char lights (layer 3) + ambient
	//   Pass 2 (layer 4):     objectes luminaris → NOMÉS llums luminaris (+ ambient)
	//   Pass 3 (layer 5):     objectes obscurios → NOMÉS llums obscurios (+ ambient)
	//   Pass 4 (layer 2): __obj → SENSE llums, renderitzat ÚLTIM
	// L'ambient està a {0,4,5} → present als passes 1/2/3. BGSphere (layer 1) el gestiona
	// TransitionController. Els 4 passes comparteixen color+depth (autoClear=false) → es
	// componen correctament i s'oclouen entre ells.
	static renderScene() {
		if (!this.renderer) return;

		// Pass 1 — character + thumbnails (layer 0), il·luminat per les char lights (layer 3)
		this.camera.layers.set(0);
		this.camera.layers.enable(3); // CHAR_LAYER: llums pròpies del character
		this.renderer.render(this.scene, this.camera);

		// A partir d'aquí componem sobre el mateix buffer (no esborrem color ni depth).
		this.renderer.autoClear = false;

		// Pass 2 — luminaris: objectes layer 4, il·luminats NOMÉS per llums layer 4 (+ ambient)
		this.camera.layers.set(4); // PROD_LAYER
		this.renderer.render(this.scene, this.camera);

		// Pass 3 — obscurios: objectes layer 5, il·luminats NOMÉS per llums layer 5 (+ ambient)
		this.camera.layers.set(5); // OBSCURIOS_LAYER
		this.renderer.render(this.scene, this.camera);

		// Pass 4 — __obj: objectes layer 2, sense llums, renderitzat ÚLTIM
		this.camera.layers.set(2); // OBJ_LAYER
		this.renderer.render(this.scene, this.camera);

		this.camera.layers.enableAll();
		this.renderer.autoClear = true;
	}

	// Quan un controlador extern (p.ex. Scene amb el seu double render) pren el
	// control del render a pantalla, desactiva el render simple d'aquí per no dibuixar dos cops.
	static _renderEnabled = true;
	static disableRender() { this._renderEnabled = false; }
	static enableRender() { this._renderEnabled = true; }

	static loop() {
		if (!this._started || this._paused) return;

		this.update();
		if (this._renderEnabled) this.render();
	}

	static resize() {
		if (isSmartphone) return;

		// Solo reseteamos el tamaño inline que deja renderer.setSize. NO vaciar todo `style`: perdería
		// el opacity (Scene.hide) y el pointerEvents (start) del canvas.
		this.domElement.style.width = '';
		this.domElement.style.height = '';
		const { width, height } = this.domElement.getBoundingClientRect();

		if (width === this.size.x && height === this.size.y) return;

		this.size.set(width, height);
		this.renderer.setSize(this.size.x, this.size.y);

		// if (this.defaults.ortho) {
		// 	this.camera.left = -this.size.x / 2;
		// 	this.camera.right = this.size.x / 2;
		// 	this.camera.top = this.size.y / 2;
		// 	this.camera.bottom = -this.size.y / 2;
		// } else {
		this.camera.aspect = this.size.x / this.size.y;

		this.cameraPosition.z = this.defaults.cameraPos.z;
		this.cameraGroup.position.z = this.defaults.cameraPos.z;

		if (this.defaults.camera2D) {
			this.camera.fov = 2 * Math.atan(this.size.x / this.camera.aspect / (2 * this.defaults.cameraPos.z)) * (180 / Math.PI);
		}
		// }

		this.camera.updateProjectionMatrix();
	}

	static dispose() {
		this.removeEventListeners();
	}
}
